import { useEffect, useState } from "react";
import { Table, Badge, Card } from "react-bootstrap";
import { User, Calendar, Edit, ArrowRight } from "lucide-react";
import { useAsync } from "../../../hooks";
import { getVariantLogs } from "../../../services/getLogs";
import { VariantLog, VariantLogsResponse } from "../../../interface";
import AppLoader from "../AppLoader";

interface LoggersListProps {
  productId: number;
}

interface VariantLogWithUser extends VariantLog {
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
}

const LoggersList = ({ productId }: LoggersListProps) => {
  const [logsState, setLogsState] = useState<VariantLogWithUser[]>([]);
  const { execute, data, loading } = useAsync<VariantLogsResponse, [number]>();

  useEffect(() => {
    if (Number.isInteger(productId)) {
      execute(getVariantLogs, productId);
    } else {
      console.error("Invalid productId");
    }
  }, [productId, execute]);

  useEffect(() => {
    if (data && data.logs) {
      const allLogs: VariantLogWithUser[] = Object.values(data.logs)
        .flat()
        .filter((log): log is VariantLogWithUser => !!log && !!log.user);
      setLogsState(allLogs);
    }
  }, [data]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <AppLoader />
      </div>
    );
  }

  if (!logsState.length) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <div className="text-muted mb-3">
            <Edit size={48} className="opacity-50" />
          </div>
          <h5 className="text-muted">No logs found</h5>
          <p className="text-muted small">
            There are no variant logs available for this product.
          </p>
        </Card.Body>
      </Card>
    );
  }

  // group logs by variantId
  const groupedLogs = logsState.reduce((acc, log) => {
    if (!acc[log.variantId]) acc[log.variantId] = [];
    acc[log.variantId].push(log);
    return acc;
  }, {} as Record<number, VariantLogWithUser[]>);

  const getActionBadge = (action: string) => {
    const actionColors: Record<string, string> = {
      CREATE: "success",
      UPDATE: "primary",
      DELETE: "danger",
      RESTORE: "info",
    };
    return actionColors[action.toUpperCase()] || "secondary";
  };

  const getRoleBadge = (role: string) => {
    const roleColors: Record<string, string> = {
      ADMIN: "danger",
      MANAGER: "warning",
      USER: "primary",
      EDITOR: "info",
    };
    return roleColors[role.toUpperCase()] || "secondary";
  };

  return (
    <div className="container-fluid px-0">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Badge bg="info" className="fs-6">
          {logsState.length} total logs
        </Badge>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <Table striped bordered hover className="mb-0 align-middle">
          <thead className="table-dark">
            <tr>
              <th className="text-center" style={{ width: "60px" }}>
                #
              </th>
              <th className="d-none d-md-table-cell">
                <User size={16} className="me-1" /> User Details
              </th>
              <th className="d-table-cell d-md-none">User</th>
              <th>
                <Edit size={16} className="me-1" /> Action
              </th>
              <th className="d-none d-lg-table-cell">Changes</th>
              <th>
                <Calendar size={16} className="me-1" /> Updated At
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedLogs).map(([_, logs], groupIndex) =>
              logs.map((log, index) => (
                <tr key={log.id}>
                  {index === 0 && (
                    <td
                      className="text-center fw-bold bg-light border-end border-primary border-3 align-top"
                      rowSpan={logs.length}
                      style={{ fontSize: "1.1rem", color: "#0d6efd" }}
                    >
                      {groupIndex + 1}
                    </td>
                  )}

                  {/* User (Desktop) */}
                  <td className="d-none d-md-table-cell">
                    <div className="d-flex flex-column">
                      <div className="fw-semibold">{log.user.name}</div>
                      <div className="text-muted small">{log.user.email}</div>
                      <Badge
                        bg={getRoleBadge(log.user.role)}
                        className="align-self-start mt-1"
                        style={{ fontSize: "0.7rem" }}
                      >
                        {log.user.role}
                      </Badge>
                    </div>
                  </td>

                  {/* User (Mobile) */}
                  <td className="d-table-cell d-md-none">
                    <div className="fw-semibold small">{log.user.name}</div>
                    <Badge
                      bg={getRoleBadge(log.user.role)}
                      className="align-self-start mt-1"
                      style={{ fontSize: "0.6rem" }}
                    >
                      {log.user.role}
                    </Badge>
                  </td>

                  {/* Action */}
                  <td>
                    <Badge
                      bg={getActionBadge(log.action)}
                      className="px-3 py-2"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {log.action}
                    </Badge>
                  </td>

                  {/* Changes (Desktop) */}
                  <td className="d-none d-lg-table-cell">
                    <div className="d-flex align-items-center small">
                      <div className="me-2">
                        <div className="text-muted">From:</div>
                        <code className="bg-light px-2 py-1 rounded text-danger">
                          {log.oldValue || "—"}
                        </code>
                      </div>
                      <ArrowRight size={14} className="text-muted mx-2" />
                      <div>
                        <div className="text-muted">To:</div>
                        <code className="bg-light px-2 py-1 rounded text-success">
                          {log.newValue || "—"}
                        </code>
                      </div>
                    </div>
                  </td>

                  {/* Updated At */}
                  <td>
                    <div className="d-flex flex-column">
                      <span className="fw-medium small">
                        {new Date(log.createdAt).toLocaleDateString("en-GB")}
                      </span>
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {new Date(log.createdAt).toLocaleTimeString("en-GB", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default LoggersList;
